/**
 * TrialBadge — sits in the navbar and renders a contextual chip showing
 * "X days left, Y questions used" or an upgrade nudge as the user
 * approaches the soft limit. Designed to be friendly first, urgent only
 * when truly blocked.
 *
 * State machine:
 *   ok       — quiet emerald chip, trial > 25% remaining
 *   nudge    — amber chip, trial < 25% remaining
 *   warning  — orange chip, < 10 questions left
 *   blocked  — rose chip, hard limit reached → opens upgrade modal
 *   paid     — slate chip, "Pro plan"
 */

import { useEffect, useState } from "react";
import { useLocation } from "react-router";
import { Sparkles, X } from "lucide-react";

import { useT } from "../../../lib/i18n";
import { fetchWorkspaceQuota, type Quota } from "../../../lib/trial";

interface TrialBadgeProps {
  workspaceId: string | null;
  /** Bumped whenever the parent wants to force a refresh (e.g. after Ask). */
  refreshKey?: number;
}

export function TrialBadge({ workspaceId, refreshKey }: TrialBadgeProps) {
  const t = useT();
  const location = useLocation();
  const [quota, setQuota] = useState<Quota | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    fetchWorkspaceQuota(workspaceId)
      .then((q) => {
        if (!cancelled) setQuota(q);
      })
      .catch(() => {
        // Silent fail — the badge just doesn't render.
      });
    return () => {
      cancelled = true;
    };
    // Refetch on pathname change so the counter updates after the
    // user navigates away from /ask (where their question consumed a
    // message). Cheap — /quota is a single-row read.
  }, [workspaceId, refreshKey, location.pathname]);

  // Refetch when the user comes back to the tab. Catches the
  // "asked five questions in a row on /ask without navigating" case
  // — they won't see the counter tick down otherwise.
  useEffect(() => {
    if (!workspaceId) return;
    function onFocus() {
      fetchWorkspaceQuota(workspaceId!)
        .then((q) => setQuota(q))
        .catch(() => undefined);
    }
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [workspaceId]);

  if (!quota) return null;

  if (quota.paid) {
    // Paid customer: keep a tiny slate dot so it's clear they're not
    // on a trial, without shouting about it.
    return (
      <span
        aria-label={t("trial.badge_paid")}
        title={t("trial.badge_paid")}
        className="flex h-7 items-center gap-1.5 rounded-full bg-slate-100 px-2.5 text-[11px] font-medium text-slate-600"
      >
        <Sparkles className="h-3 w-3" />
        Pro
      </span>
    );
  }

  const tone =
    quota.state === "blocked"
      ? "bg-rose-100 text-rose-800 hover:bg-rose-200"
      : quota.state === "warning"
        ? "bg-orange-100 text-orange-800 hover:bg-orange-200"
        : quota.state === "nudge"
          ? "bg-amber-100 text-amber-800 hover:bg-amber-200"
          : "bg-emerald-100 text-emerald-800 hover:bg-emerald-200";

  const label =
    quota.state === "blocked"
      ? t("trial.badge_blocked")
      : quota.state === "warning"
        ? t("trial.badge_warning", { remaining: quota.messages_remaining })
        : quota.state === "nudge"
          ? t("trial.badge_nudge", { remaining: quota.messages_remaining })
          : t("trial.badge_active", {
              days: quota.days_remaining,
              used: quota.messages_used,
              total: quota.messages_total,
            });

  return (
    <>
      <button
        type="button"
        onClick={() => setModalOpen(true)}
        className={[
          "flex h-7 items-center gap-1.5 rounded-full px-3 text-[11px] font-medium transition-colors",
          tone,
        ].join(" ")}
      >
        {label}
      </button>
      {modalOpen && <UpgradeModal onClose={() => setModalOpen(false)} />}
    </>
  );
}

function UpgradeModal({ onClose }: { onClose: () => void }) {
  const t = useT();
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-violet-100 text-violet-700">
            <Sparkles className="h-4 w-4" />
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700"
            aria-label={t("common.close")}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <h2 className="text-lg font-medium text-slate-900">
          {t("trial.quota_modal_title")}
        </h2>
        <p className="mt-2 text-[13px] leading-relaxed text-slate-600">
          {t("trial.quota_modal_body")}
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            {t("common.close")}
          </button>
          <button
            type="button"
            disabled
            className="rounded-lg bg-slate-900 px-4 py-2 text-sm text-white opacity-60"
          >
            {t("trial.upgrade_button")}
          </button>
        </div>
        <p className="mt-3 text-[11px] italic text-slate-400">
          {/* Stripe wiring lands when we leave research preview. For now the
              button is intentionally disabled — admin can flip `paid=true`
              on the workspace row to unblock testers. */}
          Billing integration coming soon.
        </p>
      </div>
    </div>
  );
}
